<template>
  <!-- One entry-form field's input, by its type. Shared by FormGenerator and the admin check-in
       cards' inline edits so both type, list options and format dates the same way. `compact`
       is the smaller card style. Emits input with the new value (and close when a date's calendar closes). -->
  <div v-if="field.type === 'multi-select'" class="relative">
    <select v-model="proxy" multiple :class="selectClass">
      <option :value="null" selected disabled>{{field.placeholder}}</option>
      <option v-for="(v, vi) in field.options" :key="vi" :value="v.value">{{v.label}}</option>
    </select>
    <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
      <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
    </div>
  </div>
  <div v-else-if="field.type === 'select'" class="relative">
    <select :disabled="field.disabled" v-model="proxy" :aria-label="field.label" :class="[selectClass, {'invalid-form-input': invalid}, compact ? '' : (field.classes || '')]">
      <!-- placeholder: false leaves the empty choice out (a Yes / No list always has a value) -->
      <option v-if="field.placeholder !== false" :value="null" selected disabled>{{field.placeholder}}</option>
      <option v-for="(v, vi) in field.options" :key="vi" :value="v.value" :disabled="(field.disabledOptions || []).some(disabledOption => disabledOption.value === v.value)">{{v.label}}</option>
    </select>
    <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
      <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
    </div>
  </div>
  <date-picker v-else-if="field.type === 'input' && field.inputType === 'date'" v-model="proxy" value-type="YYYY-MM-DD" :format="Localized.getDateFormat(country)" :class="{'field-input-compact-date': compact}" @close="$emit('close')"></date-picker>
  <input v-else-if="field.type === 'input' && field.inputType === 'number'" type="number" v-model.number="proxy" :placeholder="''" :aria-label="field.label" :class="[inputClass, {'invalid-form-input': invalid}]">
  <input v-else-if="field.type === 'input'" :type="field.inputType" v-model="proxy" :placeholder="''" :aria-label="field.label" :class="[inputClass, {'invalid-form-input': invalid}]">
  <textarea v-else-if="field.type === 'textArea'" v-model="proxy" :placeholder="field.label" :aria-label="field.label" :class="inputClass"/>
  <input v-else-if="field.type === 'checkbox'" v-model="proxy" :aria-label="field.label" class="mr-2 leading-tight" type="checkbox">
</template>

<script>
// The field types FieldInput renders. File, JSON and calculated fields stay with their callers.
export const FIELD_INPUT_TYPES = ['multi-select', 'select', 'input', 'textArea', 'checkbox']

export default {
  props: {
    field: {type: Object, required: true},
    value: {default: null},
    country: {type: String, default: 'US'},
    // required and empty (FormGenerator's validation)
    invalid: Boolean,
    compact: Boolean
  },
  computed: {
    proxy: {
      get () {
        return this.value
      },
      set (v) {
        this.$emit('input', v)
      }
    },
    selectClass () {
      return this.compact
        ? 'field-input-compact block appearance-none w-full bg-white border border-gray-400 text-gray-900 pl-2 pr-6 rounded leading-tight focus:outline-none focus:border-blue-500'
        : 'block appearance-none w-full bg-gray-300 border border-gray-300 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500'
    },
    inputClass () {
      return this.compact
        ? 'field-input-compact block w-full bg-white border border-gray-400 text-gray-900 px-2 rounded leading-tight focus:outline-none focus:border-blue-500'
        : 'text-input focus:outline-none focus:shadow-outline'
    }
  }
}
</script>

<style>
.field-input-compact {
  height: 1.75rem;
  font-size: 0.8125rem;
}
textarea.field-input-compact {
  height: auto;
  min-height: 3.5rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}
.field-input-compact-date.mx-datepicker {
  width: 10rem;
}
.field-input-compact-date .mx-input {
  height: 1.75rem;
  font-size: 0.8125rem;
  padding: 0 0.5rem;
}
</style>
